if (!customElements.get('madeca-customer-reviews')) {
  customElements.define('madeca-customer-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.page = 1;
      this.perPage = Math.max(1, Math.min(12, Number(this.dataset.pageSize) || 12));
      this.rating = this.querySelector('[name="rating"]');
      this.sort = this.querySelector('[name="sort"]');
      this.grid = this.querySelector('.customer-reviews__grid');
      this.status = this.querySelector('[role="status"]');
      this.previous = this.querySelector('[data-previous]');
      this.next = this.querySelector('[data-next]');
      this.retry = this.querySelector('.customer-reviews__retry');
      this.dialog = document.createElement('dialog');
      this.dialog.className = 'customer-reviews__dialog';
      this.dialog.setAttribute('aria-labelledby', `${this.id}-dialog-title`);
      this.dialog.innerHTML = `<button type="button" class="customer-reviews__dialog-close" autofocus>Close review</button><div class="customer-reviews__stars" role="img"></div><h3 id="${this.id}-dialog-title"></h3><time></time><p class="madeca-review-product"></p><div class="madeca-review-gallery"></div><p class="madeca-review-excerpt" hidden>Review excerpt</p><p class="customer-reviews__dialog-body"></p>`;
      this.append(this.dialog);
      this.dialog.querySelector('button').addEventListener('click', () => this.dialog.close());
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-read-review]');
        if (button) this.openReview(button.dataset.readReview);
      });
      [this.rating, this.sort].forEach(control => control.addEventListener('change', () => { this.page = 1; this.render(); }));
      this.previous.addEventListener('click', () => this.changePage(-1));
      this.next.addEventListener('click', () => this.changePage(1));
      this.retry.addEventListener('click', () => this.load());
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this.observer.disconnect(); this.load(); }
      }, { rootMargin: '800px' });
      this.observer.observe(this);
    }

    disconnectedCallback() { this.observer?.disconnect(); }

    async load() {
      if (this.loading || this.reviews) return;
      this.loading = true;
      this.retry.hidden = true;
      try {
        const response = await fetch(this.dataset.source);
        if (!response.ok) throw new Error('Could not load reviews');
        const data = await response.json();
        if (data.productHandle !== (this.dataset.productHandle || '345-relief-cream') || data.reviews.length !== data.count) throw new Error('Invalid review data');
        this.reviews = data.reviews.map((review, index) => ({ ...review, timestamp: Date.parse(review.date), index }));
        [this.rating, this.sort].forEach(control => { control.disabled = false; });
        this.render();
      } catch {
        this.status.textContent = `Showing the first ${this.perPage} reviews. The remaining reviews could not be loaded. Please try again.`;
        this.retry.hidden = false;
      } finally { this.loading = false; }
    }

    changePage(change) {
      this.page += change;
      this.render();
      this.querySelector('.customer-reviews__controls').scrollIntoView({ block: 'start', behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      const focusTarget = this.next.disabled ? this.previous : this.next;
      focusTarget.focus({ preventScroll: true });
    }

    openReview(id) {
      const review = this.reviews?.find(review => review.id === id);
      if (!review) return;
      const stars = this.dialog.querySelector('.customer-reviews__stars');
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      this.dialog.querySelector('h3').textContent = review.author || 'Customer';
      const date = this.dialog.querySelector('time');
      date.dateTime = review.date;
      date.textContent = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(review.date));
      this.dialog.querySelector('.customer-reviews__dialog-body').textContent = review.body || 'Rating only';
      this.dialog.querySelector('.madeca-review-product').textContent = review.productTitle || '';
      this.dialog.querySelector('.madeca-review-excerpt').hidden = !review.bodyTruncated;
      this.dialog.querySelector('.madeca-review-gallery').replaceChildren(...(review.photos || []).map(url => this.photo(url, review.author)));
      this.dialog.showModal();
    }

    render() {
      if (!this.reviews) return;
      const filtered = this.reviews.filter(review => this.rating.value === 'all' || review.rating === Number(this.rating.value));
      const sort = this.sort.value;
      filtered.sort((a, b) => {
        if (sort === 'highest') return b.rating - a.rating || b.timestamp - a.timestamp || a.index - b.index;
        if (sort === 'lowest') return a.rating - b.rating || b.timestamp - a.timestamp || a.index - b.index;
        return (sort === 'oldest' ? a.timestamp - b.timestamp : b.timestamp - a.timestamp) || a.index - b.index;
      });
      const pages = Math.max(1, Math.ceil(filtered.length / this.perPage));
      this.page = Math.max(1, Math.min(this.page, pages));
      const start = (this.page - 1) * this.perPage;
      this.grid.replaceChildren(...filtered.slice(start, start + this.perPage).map(review => this.card(review)));
      this.status.textContent = filtered.length ? `Showing ${start + 1}–${Math.min(start + this.perPage, filtered.length)} of ${filtered.length} reviews` : 'No reviews match these filters.';
      this.querySelector('[data-page]').textContent = `Page ${this.page} of ${pages}`;
      this.previous.disabled = this.page === 1;
      this.next.disabled = this.page === pages;
    }

    card(review) {
      const card = document.createElement('article');
      card.className = 'customer-reviews__card';
      card.dataset.reviewId = review.id;
      const stars = document.createElement('div');
      stars.className = 'customer-reviews__stars';
      stars.setAttribute('role', 'img');
      stars.setAttribute('aria-label', `${review.rating} out of 5 stars`);
      stars.textContent = '★'.repeat(review.rating) + '☆'.repeat(5 - review.rating);
      const header = document.createElement('header');
      const author = document.createElement('h3');
      author.textContent = review.author || 'Customer';
      const date = document.createElement('time');
      date.dateTime = review.date.slice(0, 10);
      date.textContent = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(new Date(review.date));
      header.append(author, date);
      const body = document.createElement('p');
      body.className = 'customer-reviews__body' + (review.body ? '' : ' customer-reviews__body--empty');
      body.textContent = review.body || 'Rating only';
      const read = document.createElement('button');
      read.type = 'button';
      read.className = 'customer-reviews__read';
      read.dataset.readReview = review.id;
      read.textContent = 'Read review';
      read.setAttribute('aria-label', `Read review by ${review.author || 'Customer'}`);
      read.setAttribute('aria-haspopup', 'dialog');
      const product = document.createElement('p');
      product.className = 'madeca-review-product';
      product.textContent = review.productTitle || '';
      card.append(stars, header, product);
      if (review.photos?.length) card.append(this.photo(review.photos[0], review.author));
      card.append(body, read);
      return card;
    }

    photo(url, author) {
      const image = document.createElement('img');
      image.className = 'madeca-review-photo';
      image.src = url;
      image.alt = `Customer photo from ${author || 'a customer'}`;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 400;
      image.height = 300;
      return image;
    }
  });
}
